import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowUpDown, FileText } from 'lucide-react';
import { useEvalDatasetDetail } from '@/hooks/useEvaluator';
import type { LangfuseDatasetItem } from '@/types/evaluator';

interface DatasetItemsListProps {
  orgId: string | undefined;
  datasetName: string;
  onSelectItem: (itemId: string) => void;
}

export function DatasetItemsList({ orgId, datasetName, onSelectItem }: DatasetItemsListProps) {
  const { data, isLoading, error } = useEvalDatasetDetail(datasetName, orgId);
  const [sortNewest, setSortNewest] = useState(true);

  const items = data?.items ?? [];

  const sorted = [...items].sort((a, b) => {
    const aDate = a.created_at ?? '';
    const bDate = b.created_at ?? '';
    return sortNewest ? bDate.localeCompare(aDate) : aDate.localeCompare(bDate);
  });

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-3 border-b card-border flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-heading truncate">{datasetName}</h2>
          {data?.description && (
            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{data.description}</p>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-sm text-muted-foreground">{items.length} items</span>
          {sorted.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground"
              onClick={() => setSortNewest((prev) => !prev)}
            >
              <ArrowUpDown className="mr-1 h-3 w-3" />
              {sortNewest ? 'Newest first' : 'Oldest first'}
            </Button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-auto p-4">
        {isLoading && <p className="text-sm text-muted-foreground">Loading items...</p>}
        {error && <p className="text-sm text-destructive">Failed to load dataset items.</p>}
        {!isLoading && !error && sorted.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <FileText className="h-10 w-10 text-muted-foreground/50 mb-3" />
            <p className="text-sm font-medium text-heading">No items in this dataset</p>
            <p className="text-xs text-muted-foreground mt-1">Generate personas to populate this dataset.</p>
          </div>
        )}
        <div className="space-y-2">
          {sorted.map((item: LangfuseDatasetItem) => {
            const persona = item.input?.persona ?? 'Unnamed persona';
            const goal = item.input?.goal ?? '';
            return (
              <button
                key={item.id}
                onClick={() => item.id && onSelectItem(item.id)}
                className="w-full rounded-xl border card-border card-secondary-bg p-4 text-left transition hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1 min-w-0 max-w-[70%]">
                    <div className="text-sm font-semibold text-heading truncate">{persona}</div>
                    {goal && <div className="text-xs text-muted-foreground truncate">{goal}</div>}
                  </div>
                  {item.status && (
                    <span className="shrink-0 inline-flex items-center rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400 capitalize">
                      {item.status.toLowerCase()}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
